import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const installedTransports = new WeakSet();

/**
 * Adapt the app's own AJAX request, retaining its URL, CEP format and response UI.
 * Internal requests use the original jQuery transport, not another validation API.
 */
function installNativeTransport(jquery) {
  if (!jquery?.ajaxTransport || installedTransports.has(jquery)) return;
  installedTransports.add(jquery);
  jquery.ajaxTransport('+json', (options) => {
    const owner = document.querySelector('plantch-aj-cart');
    if (!(owner instanceof PlantchAJCart) || options.plantchAJInternal) return;
    const url = new URL(options.url, location.href);
    if (!window.appURL || url.origin !== new URL(window.appURL).origin || url.pathname !== '/appjetty-zipcode') return;
    let cancel;
    return {
      send(headers, complete) { cancel = owner.checkNativeRequest(jquery, options, complete); },
      abort() { cancel?.(); },
    };
  });
}

/**
 * @typedef {object} CartRefs
 * @property {HTMLElement} widget
 * @property {HTMLElement} status
 * @property {HTMLScriptElement} items
 * @property {HTMLButtonElement} checkoutButton
 * @extends {Component<CartRefs>}
 */
class PlantchAJCart extends Component {
  requiredRefs = ['widget', 'status', 'items', 'checkoutButton'];
  #listeners;
  #observer;
  #requests = new Set();
  #revision = 0;
  #run = 0;
  #approved = false;
  #requested = false;
  #signature = '';
  #submitting = false;
  #checkoutRequest;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const signal = this.#listeners.signal;
    this.addEventListener('input', this.invalidate, { signal });
    this.addEventListener('click', this.#confirm, { capture: true, signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.invalidate, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.invalidate, { signal });
    document.addEventListener('submit', this.#checkout, { capture: true, signal });
    document.addEventListener('load', this.#mount, { capture: true, signal });
    window.addEventListener('load', this.#mount, { signal });
    this.#observeButton();
    this.invalidate();
    this.#mount();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeButton();
    this.invalidate();
    this.#mount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#observer?.disconnect();
    this.#checkoutRequest?.abort();
    this.#checkoutRequest = undefined;
    for (const request of this.#requests) request.abort();
    this.#requests.clear();
  }

  #mount = () => {
    installNativeTransport(window.jQuery);
    if (!this.refs.widget.querySelector('#zipcode_text') && typeof window.zipcode_html === 'string') {
      this.refs.widget.innerHTML = window.zipcode_html;
      const input = this.refs.widget.querySelector('#zipcode_text');
      input?.setAttribute('aria-label', this.dataset.label);
      try { if (input) input.value = localStorage.getItem('pincode_val') || ''; } catch {}
      const button = this.refs.widget.querySelector('.zipcode_submit');
      button?.setAttribute('type', 'button');
    }
    this.#syncButton();
  };

  #observeButton() {
    this.#observer?.disconnect();
    this.#observer = new MutationObserver(() => this.#syncButton());
    this.#observer.observe(this.refs.checkoutButton, { attributes: true, attributeFilter: ['disabled'] });
  }

  #syncButton() {
    const disabled = !this.#approved;
    if (this.refs.checkoutButton.disabled !== disabled) this.refs.checkoutButton.disabled = disabled;
  }

  invalidate = () => {
    this.#revision++;
    this.#approved = false;
    this.#requested = false;
    this.#signature = '';
    this.#checkoutRequest?.abort();
    this.#checkoutRequest = undefined;
    for (const request of this.#requests) request.abort();
    this.#requests.clear();
    this.refs.status.textContent = this.dataset.required;
    this.#syncButton();
  };

  #confirm = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.zipcode_submit')) return;
    this.invalidate();
    this.#requested = true;
  };

  #fingerprint(items) {
    return JSON.stringify(items.map(({ key, quantity }) => [key, quantity]).sort());
  }

  async #cart(signal) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store', signal });
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
  }

  #status(key, titles = []) {
    const text = document.createElement('p');
    text.textContent = this.dataset[key];
    this.refs.status.replaceChildren(text);
    if (!titles.length) return;
    const list = document.createElement('ul');
    for (const title of titles) {
      const item = document.createElement('li');
      item.textContent = title;
      list.append(item);
    }
    this.refs.status.append(list);
  }

  /**
   * Run the native widget request once per distinct non-exempt cart product, then return
   * an aggregate response to the app's original success/error callbacks.
   * @param {object} jquery - The app's jQuery instance.
   * @param {object} options - Original AJ request options.
   * @param {Function} complete - jQuery transport completion callback.
   * @returns {Function} Cancels this native verification.
   */
  checkNativeRequest(jquery, options, complete) {
    const controller = new AbortController();
    this.#requests.add(controller);
    const revision = this.#revision;
    const requested = this.#requested;
    const run = ++this.#run;
    const signal = controller.signal;
    const timer = setTimeout(() => controller.abort(), 45000);
    const check = async () => {
      try {
        const items = JSON.parse(this.refs.items.textContent);
        const cart = await this.#cart(signal);
        const signature = this.#fingerprint(items);
        if (!items.length || signature !== this.#fingerprint(cart.items)) throw new Error('Cart changed');
        const products = [...new Map(items.filter((item) => item.cep_exempt !== true).map((item) => [item.product_id, item])).values()];
        const results = [];
        for (const product of products) {
          if (signal.aborted) throw new Error('Cancelled');
          if (!Number.isSafeInteger(product.product_id) || product.product_id <= 0) throw new Error('Missing product ID');
          const data = new URLSearchParams(options.data);
          data.set('pid', String(product.product_id));
          data.delete('collection_id');
          data.delete('collection_id[]');
          for (const id of product.collections) data.append('collection_id[]', String(id));
          const result = await new Promise((resolve, reject) => {
            const request = jquery.ajax({
              url: options.url,
              type: 'POST',
              data: data.toString(),
              dataType: 'json',
              headers: options.headers,
              xhrFields: options.xhrFields,
              plantchAJInternal: true,
              global: false,
              timeout: 15000,
            });
            const abort = () => request.abort();
            signal.addEventListener('abort', abort, { once: true });
            request.done(resolve).fail(() => reject(new Error('AJ unavailable'))).always(() => signal.removeEventListener('abort', abort));
          });
          if (![0, 1, '0', '1'].includes(result.is_valid_pincode) || Number(result.credits) <= 0) throw new Error('Invalid AJ response');
          results.push({ product, result });
        }
        const currentCart = await this.#cart(signal);
        if (signature !== this.#fingerprint(currentCart.items)) throw new Error('Cart changed');
        if (signal.aborted || revision !== this.#revision || run !== this.#run || !this.isConnected) {
          complete(0, 'abort');
          return;
        }
        const blocked = results.filter(({ result }) => Number(result.is_valid_pincode) !== 1);
        // No AJ query or credit consumption is needed when every product is exempt.
        const response = results.length
          ? { ...(blocked[0] || results[0]).result, is_valid_pincode: blocked.length ? 0 : 1 }
          : {
              is_valid_pincode: /^\d{5}-?\d{3}$/.test((new URLSearchParams(options.data).get('pincode_val') || '').trim()) ? 1 : 0,
              is_cart_disable: 0,
              widget_visibility: 1,
              estimated_day: '',
              zip_city: '',
            };
        this.#approved = requested && Number(response.is_valid_pincode) === 1;
        this.#signature = this.#approved ? signature : '';
        complete(200, 'success', { json: response });
        if (requested && blocked.length) this.#status('blocked', blocked.map(({ product }) => product.title));
        else this.refs.status.textContent = this.#approved ? '' : this.dataset.required;
        this.#syncButton();
      } catch {
        complete(0, 'error');
        if (revision === this.#revision && run === this.#run && this.isConnected) {
          this.#approved = false;
          this.#status('error');
          this.#syncButton();
        }
      } finally {
        clearTimeout(timer);
        this.#requests.delete(controller);
      }
    };
    check();
    return () => controller.abort();
  }

  #checkout = async (event) => {
    const button = this.refs.checkoutButton;
    if (event.target !== button.form || (event.submitter && event.submitter !== button) || this.#submitting) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!this.#approved || this.#checkoutRequest) return;
    const controller = new AbortController();
    this.#checkoutRequest = controller;
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const cart = await this.#cart(controller.signal);
      if (!this.#approved || controller.signal.aborted || this.#fingerprint(cart.items) !== this.#signature) {
        this.invalidate();
        return;
      }
      this.#submitting = true;
      button.form.requestSubmit(button);
    } catch {
      if (!controller.signal.aborted) {
        this.invalidate();
        this.#status('error');
      }
    } finally {
      clearTimeout(timer);
      if (this.#checkoutRequest === controller) this.#checkoutRequest = undefined;
      this.#submitting = false;
    }
  };
}

if (!customElements.get('plantch-aj-cart')) customElements.define('plantch-aj-cart', PlantchAJCart);
